import { useState, useRef } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, User, Camera, Mail, Shield, Crown } from "lucide-react";
import { useTranslation } from "react-i18next";
import { TopNav } from "../components/layout/top-nav";
import { useAuth } from "../hooks/use-auth";
import { useMembership } from "../hooks/use-membership";
import { LanguageSwitcher } from "../components/common/language-switcher";

interface UserSettings {
  name: string;
  avatar: string;
  language: string;
}

export function SettingsPage() {
  const { t } = useTranslation();
  const auth = useAuth();
  const { currentPlan, isLoaded: isMembershipLoaded } = useMembership();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [settings, setSettings] = useState<UserSettings>(() => {
    const saved = localStorage.getItem("userSettings");
    if (saved) {
      return JSON.parse(saved);
    }
    return {
      name: auth.user?.nickname || "User",
      avatar: "",
      language: "en-US",
    };
  });
  const [saved, setSaved] = useState(false);

  // 等待认证和会员数据加载
  if (auth.loading || !isMembershipLoaded) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-purple-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  const handleAvatarClick = () => {
    fileInputRef.current?.click();
  };

  const handleAvatarChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (event) => {
        const result = event.target?.result as string;
        setSettings({ ...settings, avatar: result });
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSave = () => {
    localStorage.setItem("userSettings", JSON.stringify(settings));
    setSaved(true);
    setTimeout(() => setSaved(false), 2000);
  };

  // 获取登录方式
  const getAuthProvider = () => {
    if (!auth.user?.identities?.length) return "Email";
    if (auth.user.identities.includes("google")) return "Google";
    return "Email";
  };

  return (
    <div className="min-h-screen bg-white">
      <TopNav user={auth.user} onSignOut={auth.user ? auth.signOut : undefined} />

      <main className="pt-24 pb-16 px-4 sm:px-6 lg:px-8 max-w-2xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <Link
            to="/"
            className="inline-flex items-center gap-2 text-sm text-gray-500 hover:text-gray-900 mb-4"
          >
            <ArrowLeft className="w-4 h-4" />
            {t('settings.backToHome')}
          </Link>
          <h1 className="text-2xl font-bold text-gray-900 mb-2">{t('settings.title')}</h1>
          <p className="text-gray-500 text-sm">
            {t('settings.subtitle')}
          </p>
        </div>

        {/* Settings Form */}
        <div className="space-y-6">
          {/* Account Info */}
          <div className="bg-gray-50 rounded-xl p-6">
            <h3 className="text-sm font-medium text-gray-700 mb-4 flex items-center gap-2">
              <Shield className="w-4 h-4" />
              Account Information
            </h3>
            <div className="space-y-3">
              <div className="flex items-center justify-between py-2">
                <span className="text-sm text-gray-500 flex items-center gap-2">
                  <Mail className="w-4 h-4" />
                  Email
                </span>
                <span className="text-sm font-medium text-gray-900">
                  {auth.user?.email || "—"}
                </span>
              </div>
              <div className="border-t border-gray-200"></div>
              <div className="flex items-center justify-between py-2">
                <span className="text-sm text-gray-500">Login Method</span>
                <span className="text-sm font-medium text-gray-900">
                  {getAuthProvider()}
                </span>
              </div>
              <div className="border-t border-gray-200"></div>
              <div className="flex items-center justify-between py-2">
                <span className="text-sm text-gray-500 flex items-center gap-2">
                  <Crown className="w-4 h-4" />
                  Subscription
                </span>
                <Link
                  to="/membership"
                  className="text-sm font-medium text-blue-600 hover:text-blue-700"
                >
                  {currentPlan?.name || "Free"} →
                </Link>
              </div>
            </div>
          </div>

          {/* Avatar */}
          <div className="bg-gray-50 rounded-xl p-6">
            <label className="text-sm font-medium text-gray-700 mb-4 block">{t('settings.avatar')}</label>
            <div className="flex items-center gap-4">
              <div
                onClick={handleAvatarClick}
                className="relative w-20 h-20 bg-gray-200 rounded-full flex items-center justify-center cursor-pointer hover:opacity-80 transition overflow-hidden group"
              >
                {settings.avatar ? (
                  <img
                    src={settings.avatar}
                    alt={t('settings.avatar')}
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <User className="w-8 h-8 text-gray-400" />
                )}
                <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 flex items-center justify-center transition">
                  <Camera className="w-6 h-6 text-white" />
                </div>
              </div>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                onChange={handleAvatarChange}
                className="hidden"
              />
              <div className="text-sm text-gray-500">
                <p>{t('settings.avatarHint')}</p>
                <p className="text-xs text-gray-400 mt-1">{t('settings.avatarFormat')}</p>
              </div>
            </div>
          </div>

          {/* Username */}
          <div className="bg-gray-50 rounded-xl p-6">
            <label className="text-sm font-medium text-gray-700 mb-4 block">{t('settings.username')}</label>
            <input
              type="text"
              value={settings.name}
              onChange={(e) => setSettings({ ...settings, name: e.target.value })}
              placeholder={t('settings.usernamePlaceholder')}
              className="w-full px-4 py-3 bg-white border border-gray-200 rounded-lg text-gray-900 outline-none focus:border-gray-400 transition"
            />
          </div>

          {/* Language */}
          <LanguageSwitcher />

          {/* Save Button */}
          <div className="flex items-center gap-4 pt-4">
            <button
              onClick={handleSave}
              className="px-6 py-3 bg-gray-900 text-white rounded-lg hover:bg-gray-800 transition"
            >
              {t('settings.save')}
            </button>
            {saved && (
              <span className="text-sm text-green-600">✓ {t('settings.saved')}</span>
            )}
          </div>
        </div>
      </main>
    </div>
  );
}
